// src/pages/admin/AdminDeals.js
// Manage Deals — add, edit, approve pending, hide, delete. Same
// pattern as AdminSkills / AdminCommunity.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllDeals, updateDeal, deleteDeal, createDeal } from '../../lib/db';
import { useAuth } from '../../lib/AuthContext';
import toast from 'react-hot-toast';
import { PageBack } from '../../components/shared/PageBack';

const EMPTY = { title: '', description: '', days: 7 };

const AdminDeals = () => {
  const navigate = useNavigate();
  const { user, isTownAdmin, townAdminScope } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [form, setForm] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const all = await getAllDeals();
    setDeals(isTownAdmin ? all.filter(d => d.townId === townAdminScope) : all);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const approve = async (id) => { await updateDeal(id, { status: 'live' }); toast.success('Approved — now live'); load(); };
  const hide = async (id) => { await updateDeal(id, { status: 'hidden' }); toast.success('Hidden from public view'); load(); };
  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    await deleteDeal(id);
    toast.success('Deleted');
    load();
  };

  const startEdit = (deal) => {
    setForm({ title: deal.title || '', description: deal.description || '', days: 7 });
    setEditingId(deal.id);
    setAdding(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelForm = () => { setForm(EMPTY); setAdding(false); setEditingId(null); };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + form.days);
      if (editingId) {
        await updateDeal(editingId, { title: form.title.trim(), description: form.description.trim(), expiresAt });
        toast.success('Deal updated!');
      } else {
        const townId = isTownAdmin ? townAdminScope : (localStorage.getItem('gather-town-id') || 'loughrea');
        await createDeal({ title: form.title.trim(), description: form.description.trim(), townId, postedBy: user.uid, expiresAt });
        toast.success('Deal added!');
      }
      cancelForm();
      load();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    const date = d?.toDate ? d.toDate() : new Date(d);
    return date.toLocaleDateString('en-IE', { day: 'numeric', month: 'short' });
  };

  const filtered = deals.filter(d => statusFilter === 'all' || d.status === statusFilter);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;

  return (
    <div style={s.page}>
      <button onClick={() => navigate('/admin')} style={s.back}>← Admin</button>
      <div style={s.h1}>Deals</div>

      <button
        onClick={() => (adding ? cancelForm() : setAdding(true))}
        style={{ width: '100%', background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 14, padding: 13, fontSize: 14, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', marginBottom: 14 }}
      >
        {adding ? '✕ Cancel' : '+ Add deal'}
      </button>

      {adding && (
        <div style={{ background: 'var(--white)', borderRadius: 16, padding: 16, marginBottom: 16, boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--ink)', marginBottom: 12 }}>{editingId ? '✏️ Edit deal' : '+ New deal'}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. 20% off this weekend" />
            <textarea className="form-input" value={form.description} onChange={e => set('description', e.target.value)} placeholder="A few more details..." rows={2} style={{ resize: 'vertical' }} />
            <div>
              <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600, marginBottom: 6 }}>
                {editingId ? 'Extend by (from today):' : 'Runs for:'}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[1, 3, 7, 14].map(d => (
                  <button key={d} onClick={() => set('days', d)} style={{
                    flex: 1, padding: '8px 4px', borderRadius: 100, border: 'none', fontFamily: 'Nunito', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    background: form.days === d ? 'var(--sage-dark)' : 'var(--sage-pale)', color: form.days === d ? 'white' : 'var(--sage-dark)',
                  }}>
                    {d}d
                  </button>
                ))}
              </div>
            </div>
            <button onClick={handleSave} disabled={saving} style={{ background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 12, padding: 13, fontSize: 14, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving...' : editingId ? 'Save changes' : 'Add deal'}
            </button>
          </div>
        </div>
      )}

      <select style={s.filterSelect} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
        <option value="all">All statuses</option>
        <option value="live">Live</option>
        <option value="pending_review">Pending review</option>
        <option value="hidden">Hidden</option>
        <option value="expired">Expired</option>
      </select>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>No deals match.</div>
      ) : filtered.map(deal => (
        <div key={deal.id} style={s.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={s.title}>{deal.title}</div>
            <span style={{
              ...s.badge,
              background: deal.status === 'live' ? 'var(--sage-pale)' : deal.status === 'pending_review' ? '#FFF3CD' : '#F0F0EE',
              color: deal.status === 'live' ? 'var(--sage-deep)' : deal.status === 'pending_review' ? '#8A6D1D' : 'var(--muted)',
            }}>
              {deal.status === 'pending_review' ? 'Pending review' : deal.status === 'hidden' ? 'Hidden' : deal.status === 'expired' ? 'Expired' : 'Live'}
            </span>
          </div>
          {deal.description && <div style={s.desc}>{deal.description}</div>}
          <div style={s.meta}>Expires {fmtDate(deal.expiresAt)}</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            {deal.status !== 'live' && deal.status !== 'expired' && (
              <button style={s.approveBtn} onClick={() => approve(deal.id)}>Approve</button>
            )}
            {deal.status === 'live' && (
              <button style={s.editBtn} onClick={() => hide(deal.id)}>Hide</button>
            )}
            <button style={s.editBtn} onClick={() => startEdit(deal)}>Edit</button>
            <button style={s.deleteBtn} onClick={() => handleDelete(deal.id, deal.title)}>Delete</button>
          </div>
        </div>
      ))}
      <PageBack position="bottom" fallbackPath="/admin" />
    </div>
  );
};

const s = {
  page: { padding: '52px 20px 40px', background: 'var(--cream)', minHeight: '100vh' },
  back: { background: 'none', border: 'none', color: 'var(--sage-dark)', fontSize: 14, fontWeight: 700, cursor: 'pointer', padding: 0, marginBottom: 12 },
  h1: { fontSize: 22, fontWeight: 800, color: 'var(--ink)', marginBottom: 16 },
  filterSelect: { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #C8D9C9', fontSize: 13, fontFamily: 'Nunito', background: 'var(--white)', marginBottom: 16, boxSizing: 'border-box' },
  card: { background: 'var(--white)', borderRadius: 14, padding: 14, marginBottom: 10, border: '1.5px solid var(--border)' },
  badge: { display: 'inline-block', fontSize: 11, fontWeight: 700, borderRadius: 100, padding: '4px 10px' },
  title: { fontSize: 15, fontWeight: 800, color: 'var(--ink)' },
  desc: { fontSize: 13, color: 'var(--muted)', marginTop: 8, lineHeight: 1.5 },
  meta: { fontSize: 11.5, color: 'var(--muted)', fontWeight: 600, marginTop: 6 },
  approveBtn: { flex: 1, minWidth: 80, padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--sage-deep)', color: 'white', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: 'Nunito' },
  editBtn: { flex: 1, minWidth: 80, padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--sage-pale)', color: 'var(--sage-dark)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: 'Nunito' },
  deleteBtn: { flex: 1, minWidth: 80, padding: '9px 12px', borderRadius: 10, border: 'none', background: '#FBEAE7', color: '#C0392B', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: 'Nunito' },
};

export default AdminDeals;
